import React, { useCallback, useMemo, useState } from "react";
import {
  PiFileLight,
  PiTrash,
  PiArrowSquareOutLight,
  PiCloudArrowDownLight,
  PiDownloadLight,
  PiTrashLight,
} from "react-icons/pi";
import SSECreationProgress from "../../shared/SSECreationProgress";
import Table from "../../shared/TailwindComponents/Table";
import FilterBarWithTabs from "../../shared/TailwindComponents/FilterBarWithTabs";
import { useAppDispatch } from "../../../store";
import { showModal, hideModal } from "../../../store/actions/modalActions";

interface FileItem {
  id?: string;
  name: string;
  url?: string;
  size?: number;
  contentType?: string;
  lastModified?: string;
}

interface DataSourceFilesProps {
  t: (key: string) => string;
  filesInIndex: FileItem[];
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  deletingFiles: string[];
  handleOpenFile: (fileId: string | undefined, name: string) => void;
  handleDownloadFile: (fileId: string | undefined, name: string) => void;
  handleCopyLink: (file: FileItem) => void;
  handleDeleteFile: (fileId: string, fileName: string) => void;
  isFetchingFiles: boolean;
  filesToAdd: File[];
  setFilesToAdd: (files: File[]) => void;
  handleAddData: (files: File[]) => void;
  isFilesLoading: boolean;
  isConnecting: boolean;
  steps: any[];
  connected: boolean;
  complete: boolean;
  ok: boolean | null;
  summary?: string;
  startedAt?: number;
  isReindexing?: boolean;
}

const formatSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = -1;
  do {
    bytes = bytes / 1024;
    i++;
  } while (bytes >= 1024 && i < units.length - 1);
  return `${bytes.toFixed(1)} ${units[i]}`;
};

const ext = (name: string) => (name.split(".").pop() || "").toLowerCase();

const getFileTypeBadge = (name: string) => {
  const e = ext(name);
  const typeMap: Record<string, { label: string; color: string }> = {
    pdf: { label: "PDF", color: "red" },
    doc: { label: "DOC", color: "blue" },
    docx: { label: "DOCX", color: "blue" },
    txt: { label: "TXT", color: "gray" },
    md: { label: "MD", color: "gray" },
    xls: { label: "XLS", color: "green" },
    xlsx: { label: "XLSX", color: "green" },
    csv: { label: "CSV", color: "green" },
    jpg: { label: "JPG", color: "purple" },
    jpeg: { label: "JPEG", color: "purple" },
    png: { label: "PNG", color: "purple" },
  };
  return typeMap[e] || { label: e.toUpperCase() || "FILE", color: "gray" };
};

const prettyDate = (d?: string | number | Date) => {
  if (!d) return "-";
  const dt = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "-";
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(dt);
};

const DataSourceFiles: React.FC<DataSourceFilesProps> = ({
  t,
  filesInIndex,
  searchTerm,
  setSearchTerm,
  deletingFiles,
  handleOpenFile,
  handleDownloadFile,
  handleCopyLink,
  handleDeleteFile,
  filesToAdd,
  setFilesToAdd,
  handleAddData,
  isFilesLoading,
  isConnecting,
  steps,
  connected,
  complete,
  ok,
  summary,
  startedAt,
  isFetchingFiles,
  isReindexing,
}) => {
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const dispatch = useAppDispatch();

  // Table columns
  const columns = ["Name", "Type", "Size", "Last Modified", "Actions"];
  const sortableColumns = ["Name", "Size", "Last Modified"];

  // Map column headers to sort keys
  const columnSortMap: Record<string, string> = {
    Name: "name",
    Size: "size",
    "Last Modified": "date",
  };

  const sortedFilteredFiles = useMemo(() => {
    const filtered = filesInIndex.filter((file) =>
      file.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const sorted = [...filtered].sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;
      if (sortBy === "name" || sortBy === "Name") {
        return a.name.localeCompare(b.name) * dir;
      } else if (sortBy === "size" || sortBy === "Size") {
        return ((a.size || 0) - (b.size || 0)) * dir;
      } else {
        const ad = new Date(a.lastModified || 0).getTime();
        const bd = new Date(b.lastModified || 0).getTime();
        return (ad - bd) * dir;
      }
    });
    return sorted;
  }, [filesInIndex, searchTerm, sortBy, sortOrder]);

  // Transform file data for table
  const tableData = useMemo(() => {
    return sortedFilteredFiles.map((file) => {
      const fileType = getFileTypeBadge(file.name);
      return {
        id: file.id || file.name,
        Name: file.name,
        Type: (
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              fileType.color === "red"
                ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                : fileType.color === "blue"
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                : fileType.color === "green"
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                : fileType.color === "purple"
                ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                : "bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {fileType.label}
          </span>
        ),
        Size: formatSize(file.size),
        "Last Modified": prettyDate(file.lastModified),
        _file: file, // Keep original file data for actions
      };
    });
  }, [sortedFilteredFiles]);

  const handleSort = (column: string, order: "asc" | "desc") => {
    setSortBy(columnSortMap[column] || column);
    setSortOrder(order);
  };

  const handleRowClick = (row: any) => {
    const file = row._file as FileItem;
    if (file) {
      handleOpenFile(file.id, file.name);
    }
  };

  const tableActions = [
    {
      label: t("Open"),
      icon: <PiArrowSquareOutLight className="h-4 w-4" />,
      onClick: (row: any) => {
        const file = row._file as FileItem;
        handleOpenFile(file.id, file.name);
      },
    },
    {
      label: t("Download"),
      icon: <PiDownloadLight className="h-4 w-4" />,
      onClick: (row: any) => {
        const file = row._file as FileItem;
        handleDownloadFile(file.id, file.name);
      },
    },
    {
      label: t("Delete"),
      icon: <PiTrash className="h-4 w-4" />,
      onClick: (row: any) => {
        const file = row._file as FileItem;
        handleDeleteFile(file.id || file.name, file.name);
      },
      isDestructive: true,
      disabled: (row: any) => deletingFiles.includes(row.id),
    },
  ];

  const handleAddDataModal = useCallback(async () => {
    try {
      dispatch(
        showModal({
          showModal: true,
          type: "uploadAttachment",
          onConfirm: async (files: File[]) => {
            setFilesToAdd(files);
            await handleAddData(files);
          },
          message: t("Upload data"),
          icon: <PiCloudArrowDownLight className="w-6 h-6 text-gray-600" />,
          subMsg: t("Upload more data to your data source."),
          files: filesToAdd,
        })
      );
    } catch (error) {
      console.error(t("Error archiving task. Please try again later."), error);
    }
  }, [dispatch, filesToAdd, handleAddData, setFilesToAdd, t]);

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === tableData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((r) => r.id));
    }
  };

  const handleBulkDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    dispatch(
      showModal({
        type: "confirmation",
        message: t(`Are you sure you want to delete ${selectedRows.length} file(s)?`),
        subMsg: t("This action cannot be undone."),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          for (const id of selectedRows) {
            const file = sortedFilteredFiles.find(
              (f) => (f.id || f.name) === id
            );
            if (file) {
              handleDeleteFile(file.id || file.name, file.name);
            }
          }
          setSelectedRows([]);
          dispatch(hideModal());
        },
        onCancel: () => {},
        inputData: "Yes, Delete",
      })
    );
  }, [selectedRows, dispatch, sortedFilteredFiles, handleDeleteFile, t]);

  const filterBarActions = useMemo(() => {
    const acts: { label: string; onClick: () => void; icon: React.ReactElement }[] = [
      {
        label: t("Add Data"),
        onClick: handleAddDataModal,
        icon: <PiCloudArrowDownLight className="h-4 w-4" />,
      },
    ];
    if (selectedRows.length > 0) {
      acts.push({
        label: `${t("Delete")} (${selectedRows.length})`,
        onClick: handleBulkDelete,
        icon: <PiTrash className="h-4 w-4" />,
      });
    }
    return acts;
  }, [selectedRows, handleAddDataModal, handleBulkDelete, t]);

  if (isFilesLoading) {
    return (
      <div className="mt-4">
        <SSECreationProgress
          title={`Uploading files for index`}
          isConnecting={isConnecting}
          steps={steps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary}
          startedAt={startedAt}
          onCancel={() => {}}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col transition-all duration-300">
      <div data-tour-id="ds-files-filter-bar">
      <FilterBarWithTabs
        onSearchChange={setSearchTerm}
        actions={filterBarActions}
        noParentPadding={true}
        noParentHeight={true}
      />
      </div>

      {isReindexing && (
        <div className="mx-0 mt-2 mb-1 flex items-center gap-2 rounded-md bg-blue-50 dark:bg-blue-900/30 px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-400 border-t-transparent" />
          <span>{t('Reindexing data source… This may take a moment.')}</span>
        </div>
      )}

      <div className="flex-1 min-h-0 pb-4 relative">
        <div
          data-tour-id="ds-files-table"
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{ height: '120px' }}
        />
        <Table
          columns={columns}
          data={isFetchingFiles ? [] : tableData}
          onRowClick={handleRowClick}
          actions={tableActions}
          sortableColumns={sortableColumns}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          max="max-h-[calc(100vh-160px)]"
          min="min-h-[calc(100vh-160px)]"
          resizableColumns={true}
          storageKeyPrefix="datasource-files"
          enableSelection={true}
          selectedIds={selectedRows}
          onSelect={handleSelectRow}
          onSelectAll={handleSelectAll}
          isLoadingMore={isFetchingFiles}
          total_items={filesInIndex.length}
          total_showing_items={tableData.length}
          setIsShowing={true}
          showingLabel={t('files')}
          defaultColumnWidths={{
            Name: 300,
            Type: 100,
            Size: 100,
            "Last Modified": 150,
          }}
          emptyState={{
            icon: <PiFileLight className="h-12 w-12" />,
            headline: t("No files in this data source yet."),
            subtext: t("Upload files to get started."),
            button: {
              label: t("Add Data"),
              onClick: handleAddDataModal,
            },
          }}
        />
      </div>
    </div>
  );
};

export default DataSourceFiles;
